// Generic imports
import { useState } from 'react';
import axios from 'axios';

// MUI imports
import Dialog from '@mui/material/Dialog';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

// Local imports
import SendOpportunityDialog from './dashboard/components/SendOpportunityDialog.js';
import FollowUpDoneDialog from './FollowUpDoneDialog.js';
import { buildFollowUpEmail } from './followUp.js';
import readableId from './ReadableId.js';

// Follow up on a sent quote from the quote itself: the same follow-up email the homepage queue
// sends, or - when the follow-up happened by phone, text or outside Monada - a comment saying how.
// Either one takes the quote out of the homepage follow-up queue and shows in its comments panel.
function FollowUpDialog({ opportunity, org, me, accounts, accesstoken, emailLoginInfo, onEmailRefreshConnection, onEmailLogout, onUpdateOpportunity, onDone, onClose }) {
    const [ mode, setMode ] = useState('email');
    const [ attachPDF, setAttachPDF ] = useState(true);
    const [ email ] = useState(() => buildFollowUpEmail({ opportunity, accounts }));
    const accountName = accounts?.find(a => a.id === opportunity.account?.id)?.name || opportunity.account?.name || '';

    async function saveComment({ update }) {
        try {
            const { data: saved } = await axios.put(`/api/opportunities/${opportunity.id}?accesstoken=${accesstoken}&organizationId=${org.id}`, { opportunity: { updates: [...(opportunity.updates || []), update] } });
            onUpdateOpportunity({ opportunity: saved });
        } catch (e) {
            return false;
        }
        onDone(`Follow-up for quote ${readableId(opportunity)} marked as done`);
        return true;
    }

    if (mode === 'comment') {
        return (
            <FollowUpDoneDialog
                opportunity={opportunity}
                quoteLabel={readableId(opportunity)}
                accountName={accountName}
                me={me}
                onSave={saveComment}
                onClose={onClose}
            />
        );
    }

    return (
        // The composer lays itself out at 75vw, so let the dialog grow to fit it.
        <Dialog open={true} onClose={onClose} maxWidth={false} disableEnforceFocus>
            <Box sx={{ px: 3, pt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                <Typography sx={{ fontWeight: 600 }}>Follow up on quote {readableId(opportunity)}{accountName ? ` for ${accountName}` : ''}</Typography>
                <Button size='small' sx={{ textTransform: 'none' }} onClick={() => setMode('comment')}>
                    Followed up another way? Log it as a comment instead
                </Button>
            </Box>
            <SendOpportunityDialog
                org={org}
                accesstoken={accesstoken}
                accounts={accounts}
                emailLoginInfo={emailLoginInfo}
                onEmailRefreshConnection={onEmailRefreshConnection}
                onEmailLogout={onEmailLogout}
                opportunity={opportunity}
                toRecipients={email.toRecipients}
                initialSubject={email.subject}
                initialBody={email.body}
                subjectEditable={true}
                logFollowUp={true}
                previewMode={/^(localhost|127\.0\.0\.1)$/.test(window.location.hostname) && emailLoginInfo?.status !== 'connected'}
                attachPDF={attachPDF}
                setAttachPDF={setAttachPDF}
                onClose={onClose}
                onSent={(sentInfo) => {
                    const to = (sentInfo?.[0]?.toRecipients || email.toRecipients || []).map(r => r.address || r).filter(Boolean).join(', ');
                    onDone(`Follow-up email for quote ${readableId(opportunity)} was sent${to ? ` to ${to}` : ''}`);
                }}
            />
        </Dialog>
    );
}

export default FollowUpDialog;
